import { original, produce } from 'immer';
import {
  AsyncStatus,
  Message,
  MetaConversation,
  Tag,
} from 'lib-shared-legacy-types';
import { Score } from 'lib-shared-legacy-types/src/_temporary';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import findLast from 'lodash/findLast';
import last from 'lodash/last';
import moment from 'moment-timezone';
import { combineActions, handleActions } from 'redux-actions';

import { appActions } from 'app/app.actions';
import { isRedactionMessage } from 'app/utils/messages';
import { LanguageCode } from 'app/utils/supportedLanguages';
import {
  isMessengerMessage,
  parseMessengerMessageContent,
} from 'components/conversation/utils/messengerMessage';
import { userModalActions } from 'components/usersModal';

import { addCallerActions } from '../../shared/addCallerModal/actions/addCaller';
import { transferToAgentActions } from '../../shared/transferToAgentModal/actions/transferToAgentModal';
import { transferToNumberActions } from '../../shared/transferToNumberModal/actions/transferToNumberModal';
import { transferToQueueActions } from '../../shared/transferToQueueModal/actions/transferToQueueModal';
import { getRequestID } from '../../shared/util/request';
import { actions } from '../actions/conversation';
import { feedbackActions } from '../actions/feedback';
import { satisfactionActions } from '../actions/satisfaction';
import { updateUserInMetaConversation } from '../utils/updateUserInMetaConversation';

import { sortMessages } from './messages.utils';

const {
  transferToNumberAssistedResolveSucceeded,
  transferToNumberWsResponse,
  transferToNumberBlindRequestSucceeded,
} = transferToNumberActions;
const { transferToQueueModalRequestSucceeded, transferToQueueWsResponse } =
  transferToQueueActions;
const { transferToAgentAssistedResolveSucceeded, transferToAgentWsResponse } =
  transferToAgentActions;
const { feedbackRequest, feedbackRequestSucceeded, feedbackRequestFailed } =
  feedbackActions;
const {
  addCallerSetOnHoldRequest,
  addCallerSetOnHoldRequestSucceeded,
  addCallerSetOnHoldRequestFailed,
} = addCallerActions;
const {
  getAvailableSatisfactionLanguagesRequest,
  getAvailableSatisfactionLanguagesRequestSucceeded,
  getAvailableSatisfactionLanguagesRequestFailed,

  cancelSatisfactionRequest,
  cancelSatisfactionRequestSucceeded,
  cancelSatisfactionRequestFailed,
} = satisfactionActions;

// Destructure the Conversation actions.
// We export because of the tests.
export const {
  conversationGet,
  conversationGetSucceeded,
  conversationGetFailed,

  conversationGetBeforeSucceeded,

  conversationSubmitMessage,
  conversationSubmitMessageSucceeded,
  conversationSubmitMessageFailed,
  conversationAddSubmittedMessage,

  conversationClose,
  conversationCloseSucceeded,
  conversationCloseFailed,
  conversationWsClose,

  conversationRespondeToOfferAcceptedSucceeded,

  conversationLinkChild,
  conversationLinkChildSucceeded,
  conversationLinkChildFailed,

  conversationAddWrapup,
  conversationAddWrapupSucceeded,
  conversationAddWrapupFailed,

  conversationClaim,
  conversationClaimSucceeded,
  conversationClaimFailed,
  conversationClaimCanceled,

  conversationAddMessage,
  conversationAnonymizeMessage,
  conversationMergeMessage,
  conversationSaveTempMessage,
  conversationSaveTempFormValues,
  conversationSaveTempWrapup,
  conversationSaveTempCurrentTab,
  conversationAddRating,

  conversationWsIntegrationLoaded,

  conversationUnmount,

  conversationTag,
  conversationTagSucceeded,
  conversationTagFailed,

  conversationUntag,
  conversationUntagSucceeded,
  conversationUntagFailed,

  conversationToPending,
  conversationToPendingSucceeded,
  conversationToPendingFailed,

  conversationCancelPending,
  conversationCancelPendingSucceeded,
  conversationCancelPendingFailed,

  conversationPauseCallRecording,
  conversationPauseCallRecordingSucceeded,
  conversationPauseCallRecordingFailed,

  conversationResumeCallRecording,
  conversationResumeCallRecordingSucceeded,
  conversationResumeCallRecordingFailed,

  conversationWsPending,
  conversationWsAwaitingPending,
  conversationWsCancelPending,
  conversationWsOpened,
  conversationWsAgentAssigned,
  conversationWsAgentUnassigned,
  conversationWsReassignmentStatus,
  conversationWsEndUserPresence,
  conversationWsLanguageUpdated,

  conversationWsTagged,
  conversationWsUntagged,

  conversationWsQueued,

  conversationWsNewPartyAdded,
  conversationWsNewPartyRemoved,
  conversationWsNewPartyRinging,
  conversationWsNewPartyBusy,
  conversationWsNewPartyFailed,
  conversationWsNewPartyNoAnswer,

  conversationWsMessageDeliveryStatusUpdated,

  conversationWsOutboundMessageStatusUpdated,

  conversationUpdateUser,
  conversationSetFocusMode,
  conversationSetSmallLayoutSidebarOpen,

  conversationSetSummary,
  conversationSubjectUpdate,
} = actions;

export type ConversationStateT = {
  asyncStatus: {
    getConversation: AsyncStatus;
    getOutboundAddress: AsyncStatus;
    submit: AsyncStatus;
    close: AsyncStatus;
    wrapup: AsyncStatus;
    claim: AsyncStatus;
    tags: AsyncStatus;
    pending: AsyncStatus;
    recording: AsyncStatus;
    linked: AsyncStatus;
    feedback: AsyncStatus;
    availableSatisfactionLanguages: AsyncStatus;
    cancelSatisfaction: AsyncStatus;
    holdCaller: { [userid: string]: AsyncStatus };
  };
  isFocusMode: boolean;
  isSmallLayoutSidebarOpen: boolean;
  metaConversation: MetaConversation;
  linkedConversations?: Array<any>;
  outboundAddress: string | null;
  feedback?: Array<any>;
  availableSatisfactionLanguages?: Array<LanguageCode>;
  lastId: any | null;
};

export type ConversationState = ConversationStateT;

function newMessage(payload: any): Message {
  const { eventMessage, authors, lastMessage } = payload;

  let author: any | null = null;
  let text = '';
  let isSameSender = false;

  if (eventMessage.requested_by) {
    author = authors[eventMessage.requested_by];
  } else if (eventMessage.author_id) {
    author = authors[eventMessage.author_id];
  } else if (eventMessage.agent_id) {
    author = authors[eventMessage.agent_id];
  } else if (eventMessage.closed_by) {
    author = authors[eventMessage.closed_by];
  } else if (eventMessage.to_user_id) {
    author = authors[eventMessage.to_user_id];
  } else if (eventMessage.author) {
    author = eventMessage.author;
  }

  if (isMessengerMessage(eventMessage)) {
    text = parseMessengerMessageContent(eventMessage);
  } else if (eventMessage.text) {
    text = eventMessage.text;
  } else if (eventMessage.message) {
    text = eventMessage.message;
  }

  if (author === undefined || author === null) {
    author = {
      id: getRequestID(),
      name: '',
      email: '',
      phone_number: '',
    };
  }

  if (lastMessage) {
    // There is a message before this one.
    if (
      lastMessage.author &&
      lastMessage.author.id === eventMessage.author_id
    ) {
      // is it the same sender.
      if (eventMessage.channel === 'email') {
        isSameSender = false;
      } else if (
        lastMessage.type === 'conversation:message_added' ||
        lastMessage.type === 'conversation:add_message'
      ) {
        const timeDifference =
          new Date(eventMessage.created_at).getTime() -
          new Date(lastMessage.created_at).getTime();
        if (timeDifference <= 30000) {
          isSameSender = true;
        }
      }
    }
  }

  return {
    // Assign a default id to use as the `key` when messages are repeated.
    id: eventMessage.id !== undefined ? eventMessage.id : getRequestID(),
    external_id: eventMessage.external_id ?? null,
    request_id:
      eventMessage.requestID !== undefined ? eventMessage.requestID : null,
    frontend_id:
      eventMessage.frontend_id !== undefined ? eventMessage.frontend_id : null,
    requested_by:
      eventMessage.requested_by !== undefined ? eventMessage.requested_by : '',
    created_at:
      eventMessage.created_at !== undefined ? eventMessage.created_at : null,
    scheduled_for: eventMessage?.scheduled_for,
    csat_look_and_feel: eventMessage?.csat_look_and_feel,
    comment: eventMessage?.comment,
    score: (eventMessage.score !== undefined &&
    eventMessage.score > 0 &&
    eventMessage.score < 6
      ? parseInt(eventMessage.score)
      : null) as Score | null,
    to_list_v2:
      eventMessage.to_list_v2 !== undefined
        ? JSON.parse(eventMessage.to_list_v2)
        : null,
    reply_default_cc_emails:
      eventMessage.reply_default_cc_emails !== undefined
        ? JSON.parse(eventMessage.reply_default_cc_emails)
        : null,
    cc_list_v2:
      eventMessage.cc_list_v2 !== undefined
        ? JSON.parse(eventMessage.cc_list_v2)
        : null,
    bcc_list_v2:
      eventMessage.bcc_list_v2 !== undefined
        ? JSON.parse(eventMessage.bcc_list_v2)
        : null,
    type: eventMessage.type !== undefined ? eventMessage.type : '',
    is_redaction: isRedactionMessage(eventMessage),
    text: text,
    language: eventMessage.language,
    content_type:
      eventMessage.content_type !== undefined
        ? eventMessage.content_type
        : null,
    voicemail_url:
      eventMessage.voicemail_url !== undefined
        ? eventMessage.voicemail_url
        : null,
    recording_url:
      eventMessage.recording_url !== undefined
        ? eventMessage.recording_url
        : null,
    a_phone_number:
      eventMessage.a_phone_number !== undefined
        ? eventMessage.a_phone_number
        : null,
    b_phone_number:
      eventMessage.b_phone_number !== undefined
        ? eventMessage.b_phone_number
        : null,

    to_user_id:
      eventMessage.to_user_id !== undefined ? eventMessage.to_user_id : null,

    to: eventMessage.to !== undefined ? eventMessage.to : null,
    from: eventMessage.from !== undefined ? eventMessage.from : null,
    from_contact_point:
      eventMessage.from_contact_point !== undefined
        ? JSON.parse(JSON.stringify(eventMessage.from_contact_point))
        : null,
    resolved_result:
      eventMessage.resolved_result !== undefined
        ? eventMessage.resolved_result
        : null,

    direction:
      eventMessage.direction !== undefined
        ? eventMessage.direction.toLowerCase()
        : null,
    author_id:
      eventMessage.author_id !== undefined ? eventMessage.author_id : null,
    channel: eventMessage.channel !== undefined ? eventMessage.channel : null,
    duration:
      eventMessage.duration !== undefined
        ? parseInt(eventMessage.duration, 10)
        : null,
    agent_id:
      eventMessage.agent_id !== undefined ? eventMessage.agent_id : null,

    transfer_type:
      eventMessage.transfer_type !== undefined
        ? eventMessage.transfer_type
        : null,
    transfer_target_name:
      eventMessage.target_name !== undefined ? eventMessage.target_name : null,

    hasReceivedResponse: eventMessage.created_at !== undefined,
    isSameSender: isSameSender,
    failedResponse: false,
    author: author,
    inline_images: eventMessage.inline_images
      ? eventMessage.inline_images
      : eventMessage.inline_images_v2
        ? JSON.parse(eventMessage.inline_images_v2)
        : null,
    attached_files: eventMessage.attached_files
      ? eventMessage.attached_files
      : eventMessage.attached_files_v2
        ? JSON.parse(eventMessage.attached_files_v2)
        : null,
    email_original_content_url:
      eventMessage.email_original_content_url !== undefined
        ? eventMessage.email_original_content_url
        : null,
    email_send_error:
      eventMessage.email_send_error !== undefined
        ? eventMessage.email_send_error
        : null,
    email_send_error_description:
      eventMessage.email_send_error_description !== undefined
        ? eventMessage.email_send_error_description
        : null,
    email_send_error_smtp_response:
      eventMessage.email_send_error_smtp_response !== undefined
        ? eventMessage.email_send_error_smtp_response
        : null,
    chat_menu_choice_label: eventMessage.chat_menu_choice_label,
    chat_menu_text: eventMessage.chat_menu_text,
    chat_input_question: eventMessage.chat_input_question,
    chat_input_answer: eventMessage.chat_input_answer,
    template_name: eventMessage.template_name ?? null,
    is_auto_reply: eventMessage.is_auto_reply === 'true',
    rendered_template_name: eventMessage.rendered_template_name,
    is_story:
      eventMessage.is_story !== undefined
        ? eventMessage.is_story === 'true'
        : null,
    // Custom forms specifics
    form_id: eventMessage.form_id,
    form_version: eventMessage.form_version,
    form_submission_id: eventMessage.form_submission_id,
    messenger_choices: eventMessage.messenger_choices,
    messenger_title: eventMessage.messenger_title,
    anonymized_at: eventMessage.anonymized_at,
    conversation_value_metric_score:
      eventMessage.conversation_value_metric_score,
    source_links: eventMessage.source_links
      ? JSON.parse(eventMessage.source_links)
      : null,
    is_transcript:
      eventMessage.is_transcript !== undefined
        ? eventMessage.is_transcript.toLowerCase() === 'true'
        : null,
    transcription_utterance_start_time_ms:
      eventMessage.transcription_utterance_start_time_ms,
    transcription_utterance_end_time_ms:
      eventMessage.transcription_utterance_end_time_ms,
    email_headers: eventMessage.email_headers,
    auto_redaction_failure_reason:
      eventMessage.auto_redaction_failure_reason ?? null,
  };
}

export function emptyMetaConversation(): MetaConversation {
  const empty: MetaConversation = {
    conversation: {
      assignment: {
        assigned_at: null,
        unassigned_at: null,
        agent_id: null,
        agent: null,
      },
      assignments: [],
      async_integrations: [],
      attributes: {},
      authors: {},
      closed_at: null,
      closed_by: null,
      created_at: null,
      direction: null,
      events: [],
      id: null,
      initial_channel: null,
      live_assesment: null,
      is_abandoned: false,
      is_closed: true,
      last_seen: null,
      org_id: null,
      queue: null,
      queue_at: null,
      ratings: [],
      requested_by: null,
      tags: [],
      pending_expiration_at: null,
      pending_at: '',
      pending_by: '',
      status: null,
      is_callback_consumed: false,
      last_message_created_at: '',
      message_delivery_status: {},
      queued_at: null,
    },
    isAssignedToMe: false,
    lastSeen: null,
    integrations: [],
    messages: [],
    unread: 0,
    tempWrapup: '',
    tempMessage: null,
    tempFormValues: null,
    tempCurrentTab: undefined,
    requester: null,
    parentConversation: null,
    parentConversationRequester: null,
    previousConversations: null,
    linkedConversations: [],
    feedback: [],
    extraParties: {},
    callersOnHold: [],
  };

  return Object.assign({}, empty) as any;
}

export const initialState: ConversationStateT = {
  asyncStatus: {
    getConversation: 'succeeded',
    getOutboundAddress: 'succeeded',
    submit: 'succeeded',
    close: 'succeeded',
    wrapup: 'succeeded',
    claim: 'succeeded',
    tags: 'succeeded',
    pending: 'succeeded',
    recording: 'succeeded',
    linked: 'succeeded',
    feedback: 'succeeded',
    availableSatisfactionLanguages: 'succeeded',
    cancelSatisfaction: 'succeeded',
    holdCaller: {},
  },
  isFocusMode: false,
  isSmallLayoutSidebarOpen: false,
  metaConversation: emptyMetaConversation(),
  outboundAddress: null,
  lastId: null,
};

// Reducer
const conversation = handleActions<ConversationStateT, any>(
  {
    // transfer to number assisted transfer resolved.
    [transferToNumberAssistedResolveSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const csid = action.payload.data.csid;

      if (
        state.metaConversation.conversation.id !== csid &&
        action.payload.data.transfer_type === 'assisted'
      ) {
        const accepted = action.payload.data.accepted;
        const isAssignedToMe = state.metaConversation.isAssignedToMe;
        const nextIsAssignedToMe =
          isAssignedToMe === false || accepted === false;

        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.isAssignedToMe = nextIsAssignedToMe;
        });
      }
      return state;
    },

    //@TODO transfer to agent assisted transfer resolved.
    [transferToQueueModalRequestSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const csid = action.payload.meta.request.csid;
      if (state.metaConversation.conversation.id === csid) {
        const { id, name, is_restricted } =
          action.payload.meta.request.requestToQueue;
        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.isAssignedToMe = false;
          draft.metaConversation.conversation.assignment = null;
          draft.metaConversation.conversation.is_closed = false;
          draft.metaConversation.conversation.status = 'open';
          draft.metaConversation.conversation.queue = {
            id,
            name,
            is_restricted,
          };
        });
      }
      return state;
    },

    [userModalActions.user.update.fulfilled.type]: (
      state: ConversationState,
      action,
    ) => {
      const {
        payload: { data: updatedUser },
      } = action as ReturnType<typeof userModalActions.user.update.fulfilled>;
      const {
        requester,
        conversation: { authors },
      } = state.metaConversation;

      if (
        requester !== undefined &&
        requester !== null &&
        requester.id === updatedUser.id
      ) {
        const nextAuthors = {
          ...authors,
          [updatedUser.id.toString()]: updatedUser,
        };

        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.requester = updatedUser;
          draft.metaConversation.conversation.authors = nextAuthors;
        });
      }
      return state;
    },

    [conversationWsIntegrationLoaded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { conversation, integrations } = state.metaConversation;

      if (
        conversation.id === action.payload.params.csid ||
        // If conversation is still loading, likely ws event received before conversation was fetched.
        (!conversation.id && state.asyncStatus.getConversation === 'loading')
      ) {
        const integration = { ...action.payload.params };
        const index = integrations.findIndex((i) => i.id === integration.id);
        const nextIntegrations =
          index === -1
            ? [...integrations, integration]
            : integrations.map((i) =>
                i.id === integration.id ? integration : i,
              );

        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.integrations = nextIntegrations;
        });
      }
      return state;
    },

    // Conversation.
    [conversationGet.toString()]: (state: ConversationState, action) => {
      let lastId: any = null;
      if (state.metaConversation.conversation) {
        lastId = state.metaConversation.conversation.id;
      }
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.getConversation = 'loading';
        draft.metaConversation = emptyMetaConversation();
        draft.lastId = lastId;
      });
    },

    [conversationGetBeforeSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const events = action.payload.metaConversation.conversation.events
        ? action.payload.metaConversation.conversation.events
        : [];
      const metaConversation = emptyMetaConversation();
      const conversation = Object.assign(
        {},
        metaConversation.conversation,
        action.payload.metaConversation.conversation,
      );

      const messages: any = [];
      let lastMessage: any = null;
      let isAssignedToMe = false;

      if (events.length > 0) {
        for (const event of events) {
          // Authors can be undefined but because we checked eventas before we do not have to check authors too.
          const message = newMessage({
            eventMessage: event,
            authors: conversation.authors,
            lastMessage: lastMessage,
          });
          lastMessage = message;
          messages.push(message);
        }
      }

      if (
        conversation.assignment !== null &&
        action.payload.userID === conversation.assignment.agent_id
      ) {
        isAssignedToMe = true;
      }

      metaConversation.isAssignedToMe = isAssignedToMe;
      metaConversation.conversation = conversation;
      metaConversation.messages = messages;
      metaConversation.tempMessage =
        action.payload.metaConversation.tempMessage;
      metaConversation.tempFormValues =
        action.payload.metaConversation.tempFormValues;
      metaConversation.tempWrapup = action.payload.metaConversation.tempWrapup;
      metaConversation.tempCurrentTab =
        action.payload.metaConversation.tempCurrentTab;
      metaConversation.lastSeen = action.payload.metaConversation.lastSeen;
      metaConversation.parentConversation = action.payload.metaConversation
        .parentConversation
        ? Object.assign({}, action.payload.metaConversation.parentConversation)
        : null;
      metaConversation.parentConversationRequester = action.payload
        .metaConversation.parentConversationRequester
        ? Object.assign(
            {},
            action.payload.metaConversation.parentConversationRequester,
          )
        : null;
      metaConversation.linkedConversations = action.payload.metaConversation
        .linkedConversations
        ? action.payload.metaConversation.linkedConversations
        : [];
      metaConversation.requester = Object.assign(
        {},
        action.payload.metaConversation.requester,
      );
      metaConversation.integrations = action.payload.metaConversation
        .integrations
        ? action.payload.metaConversation.integrations
        : [];
      metaConversation.previousConversations = action.payload.metaConversation
        .previousConversations
        ? action.payload.metaConversation.previousConversations
        : [];

      return { ...state, metaConversation: metaConversation };
    },

    [conversationGetSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const events = action.payload.data.history.main_conversation.events;
      const conversation = Object.assign(
        {},
        action.payload.data.history.main_conversation,
      );
      const oldMetaConversation = Object.assign({}, state.metaConversation);
      const messages: any[] = [];
      let lastMessage: any = null;
      let isAssignedToMe = false;
      let isMessageDividerAdded = false;
      let metaConversation: any = null;
      // Are we already on the same conversation ?
      if (oldMetaConversation.conversation.id === conversation.id) {
        metaConversation = Object.assign({}, oldMetaConversation);
      } else {
        metaConversation = emptyMetaConversation();
      }

      for (const event of events) {
        // Add a new missied message divider.
        if (
          isMessageDividerAdded === false &&
          state.metaConversation.lastSeen != null &&
          new Date(event.created_at).getTime() > state.metaConversation.lastSeen
        ) {
          isMessageDividerAdded = true;
          const eventMessageDevider = {
            type: 'newMissedMessagesDevider',
          };
          const messageDivider = newMessage({
            eventMessage: eventMessageDevider,
            authors: conversation.authors,
            lastMessage: lastMessage,
          });
          messages.push(messageDivider); // Add the new message divider.
        }

        const message = newMessage({
          eventMessage: event,
          authors: conversation.authors,
          lastMessage: lastMessage,
        });

        lastMessage = message;
        messages.push(message);
      }

      if (
        conversation.assignment &&
        action.payload.meta.userID === conversation.assignment.agent_id
      ) {
        isAssignedToMe = true;
      }

      // Is it a internal call started by ourself?
      if (
        action.payload.meta.userID === conversation.requested_by &&
        conversation.direction === 'internal'
      ) {
        isAssignedToMe = true;
      }

      // Verify if integrations already received via ws event for current conversation.
      const integrations = state.metaConversation.integrations?.filter(
        (x) => x.csid === conversation.id,
      );
      if (integrations?.length > 0) {
        metaConversation.integrations = integrations;
      }

      metaConversation.messages = messages;
      metaConversation.conversation = conversation;
      ((metaConversation.requester =
        conversation.authors[conversation.requested_by] !== undefined
          ? conversation.authors[conversation.requested_by]
          : null),
        (metaConversation.isAssignedToMe = isAssignedToMe));
      metaConversation.parentConversation =
        action.payload.data.history.linked_to &&
        action.payload.data.history.linked_to.conversation
          ? action.payload.data.history.linked_to.conversation
          : null;
      metaConversation.parentConversationRequester =
        action.payload.data.history.linked_to &&
        action.payload.data.history.linked_to.requester
          ? action.payload.data.history.linked_to.requester
          : null;
      metaConversation.linkedConversations =
        action.payload.data.history.linked_conversations;
      metaConversation.previousConversations =
        action.payload.data.requester_conversations;

      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, getConversation: 'succeeded' },
        metaConversation: metaConversation,
      };
    },

    [conversationUnmount.toString()]: (state: ConversationState) => {
      return { ...state, metaConversation: emptyMetaConversation() };
    },

    [conversationGetFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, getConversation: 'failed' },
      };
    },

    [conversationClaim.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, claim: 'loading' },
      };
    },

    [conversationTag.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'loading' },
      };
    },

    [conversationTagSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      if (action.payload.request && action.payload.request.payload) {
        const { csid } = action.payload.request.payload;
        if (state.metaConversation.conversation.id === csid) {
          const tags =
            // Copy tags
            state.metaConversation.conversation.tags?.slice() as Tag[];
          const newTag = action.payload.data;
          if (!tags.map((t) => t.name).includes(newTag.name)) {
            tags.push(action.payload.data);
          }
          return {
            ...state,
            asyncStatus: { ...state.asyncStatus, tags: 'succeeded' },
            metaConversation: {
              ...state.metaConversation,
              conversation: {
                ...state.metaConversation.conversation,
                tags: tags,
              },
            },
          };
        }
        return {
          ...state,
          asyncStatus: { ...state.asyncStatus, tags: 'succeeded' },
        };
      }
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'succeeded' },
      };
    },
    [conversationWsTagged.toString()]: (state: ConversationState, action) => {
      const { csid, tag } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        const tags = state.metaConversation.conversation.tags?.slice() as Tag[]; // Copy tags
        if (!tags.map((t) => t.name).includes(tag.name)) {
          tags.push({
            name: tag.name,
            id: tag.id,
            color: tag.color,
            is_deactivated: false,
            usage_count: 0,
            type: 'conversation',
          });
        }
        return {
          ...state,
          asyncStatus: { ...state.asyncStatus, tags: 'loading' },
          metaConversation: {
            ...state.metaConversation,
            conversation: {
              ...state.metaConversation.conversation,
              tags: tags,
            },
          },
        };
      }
      return state;
    },

    [conversationWsUntagged.toString()]: (state: ConversationState, action) => {
      const currentTags =
        state.metaConversation.conversation.tags?.slice() as Tag[]; // Copy tags
      const { tag_name } = action.payload;

      const tags = currentTags.filter((tag: Tag) => tag.name !== tag_name);
      return {
        ...state,
        metaConversation: {
          ...state.metaConversation,
          conversation: {
            ...state.metaConversation.conversation,
            tags: tags,
          },
        },
      };
    },

    [conversationTagFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'failed' },
      };
    },

    [conversationUntag.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'loading' },
      };
    },

    [conversationUntagSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const currentTags =
        state.metaConversation.conversation.tags?.slice() as Tag[]; // Copy tags
      const tagToRemove = action.payload.data;
      const tags = currentTags.filter(
        (tag: Tag) => tag.name !== tagToRemove.name,
      );

      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'succeeded' },
        metaConversation: {
          ...state.metaConversation,
          conversation: { ...state.metaConversation.conversation, tags: tags },
        },
      };
    },

    [conversationUntagFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, tags: 'failed' },
      };
    },

    [conversationWsQueued.toString()]: (state: ConversationState, action) => {
      if (action.payload.csid !== state.metaConversation?.conversation?.id) {
        return state;
      }

      const queue = action.payload.queue
        ? {
            id: action.payload.queue.id,
            name: action.payload.queue.name,
            is_restricted: false,
          }
        : null;

      return {
        ...state,
        metaConversation: {
          ...state.metaConversation,
          conversation: {
            ...state.metaConversation.conversation,
            queue: queue,
          },
        },
      };
    },

    [conversationClaimSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;
        const { csid, accepted_by } = action.payload.data;

        if (draftConversation.id === csid && accepted_by !== null) {
          if (draftConversation.assignment !== null) {
            draftConversation.assignment.agent = accepted_by;
            draftConversation.assignment.agent_id = accepted_by.id;
            draftConversation.assignment.assigned_at = moment.utc().format();
          } else {
            draftConversation.assignment = {
              agent: accepted_by,
              agent_id: accepted_by.id,
              assigned_at: moment.utc().format(),
            };
          }
          draftMetaConv.isAssignedToMe = true;
        }

        draft.asyncStatus.claim = 'succeeded';
      });
    },

    [conversationClaimFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, claim: 'failed' },
      };
    },

    [conversationClaimCanceled.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, claim: 'succeeded' },
      };
    },

    [conversationSubmitMessage.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, submit: 'loading' },
      };
    },

    [conversationAddSubmittedMessage.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid } = action.payload;

      if (state.metaConversation.conversation.id !== csid) {
        return state;
      }

      const lastMessage =
        state.metaConversation.messages[
          state.metaConversation.messages.length - 1
        ] !== undefined
          ? state.metaConversation.messages[
              state.metaConversation.messages.length - 1
            ]
          : null;

      const payload = Object.assign({}, action.payload, {
        cc_list_v2: JSON.stringify(action.payload.cc_list_v2),
        bcc_list_v2: JSON.stringify(action.payload.bcc_list_v2),
      });

      const submittedMessage: any = newMessage({
        eventMessage: payload,
        lastMessage: lastMessage,
      });

      return {
        ...state,
        metaConversation: {
          ...state.metaConversation,
          messages: [...state.metaConversation.messages, submittedMessage],
        },
      };
    },

    [conversationAddMessage.toString()]: (state: ConversationState, action) => {
      return produce(state, (draft: ConversationState) => {
        if (
          action.payload.csid &&
          draft.metaConversation.conversation.id === action.payload.csid
        ) {
          const messages: any = original(draft.metaConversation.messages);
          const lastMessage: Message | null = last(messages) ?? null;

          const authors = {};
          if (action.payload.author) {
            authors[action.payload.author.id] = action.payload.author;
          }

          /**
           * There can be multiple messages of type 'conversation:rated',
           * because they can come via WS with partial information.
           *
           * In such case, we need to coalesce them into one.
           */
          const existingRating = messages.find(
            (m) => m.type === 'conversation:rated',
          );
          if (
            existingRating &&
            action.payload.message.type === 'conversation:rated'
          ) {
            const { score, comment } = action.payload.message;
            if (0 > score || score > 5) {
              return;
            }
            const updatedRating = { ...existingRating };
            updatedRating.score = score || updatedRating.score;
            updatedRating.comment = comment || updatedRating.comment;

            const updatedMessages = messages.filter(
              (m) => m.type !== 'conversation:rated',
            );
            updatedMessages.push(updatedRating);

            draft.metaConversation.messages = updatedMessages;
          } else {
            // Skip if a message with the same id is already present.
            if (
              action.payload.message.id !== undefined &&
              messages.some((m) => m.id === action.payload.message.id)
            ) {
              return;
            }

            const submittedMessage = newMessage({
              eventMessage: action.payload.message,
              lastMessage,
              authors,
            });

            draft.metaConversation.messages.push(submittedMessage as any);
            if (
              submittedMessage.created_at &&
              lastMessage?.created_at &&
              submittedMessage.created_at < lastMessage.created_at
            ) {
              draft.metaConversation.messages.sort(sortMessages);
            }
          }
        }
      });
    },

    [conversationAnonymizeMessage.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, message_id, anonymized_at } = action.payload;

      if (csid && csid == state.metaConversation.conversation.id) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            messages: state.metaConversation.messages.map(
              (message: Message) => {
                if (message.id === message_id) {
                  return {
                    id: message.id,
                    channel: message.channel,
                    created_at: message.created_at,
                    type: message.type,
                    author: message.author,
                    author_id: message.author_id,
                    isSameSender: message.isSameSender,
                    failedResponse: message.failedResponse,
                    hasReceivedResponse: message.hasReceivedResponse,
                    is_auto_reply: message.is_auto_reply,
                    is_redaction: message.is_redaction,
                    anonymized_at,
                  };
                }

                return message;
              },
            ),
          },
        };
      }

      return state;
    },

    [conversationMergeMessage.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, message: serverMessage } = action.payload;

      if (csid && csid == state.metaConversation.conversation.id) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            messages: state.metaConversation.messages.map(
              (message: Message) => {
                if (message.frontend_id === serverMessage.frontend_id) {
                  const { attached_files_v2, to_list_v2, ...rest } =
                    serverMessage;

                  return {
                    ...message,
                    ...rest,
                    ...(attached_files_v2 && {
                      attached_files: JSON.parse(attached_files_v2),
                    }),
                    ...(to_list_v2 && {
                      to_list_v2: JSON.parse(to_list_v2),
                    }),
                  };
                }

                return message;
              },
            ),
          },
        };
      }

      return state;
    },

    [conversationSubmitMessageSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft) => {
        let messages = draft.metaConversation.messages;

        draft.metaConversation.conversation.message_delivery_status = {
          ...draft.metaConversation.conversation.message_delivery_status,
          [action.payload.data.id]: {
            status: 'sent',
            timestamp: action.payload.data.created_at,
            additional_info: '',
          },
        };

        if (action.payload.responseID != null) {
          const message = findLast(draft.metaConversation.messages, {
            request_id: action.payload.responseID,
          });

          if (message !== undefined) {
            message.hasReceivedResponse = true;
            messages = draft.metaConversation.messages.map((m) =>
              m.requestID === action.payload.responseID ? message : m,
            );
          }
        }

        draft.asyncStatus.submit = 'succeeded';
        draft.metaConversation.messages = messages;
      });
    },

    [conversationSubmitMessageFailed.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft) => {
        let messages = draft.metaConversation.messages;

        if (action.payload.responseID != null) {
          const message = findLast(draft.metaConversation.messages, {
            request_id: action.payload.responseID,
          });

          if (message !== undefined) {
            message.hasReceivedResponse = true;
            message.failedResponse = true;
            messages = draft.metaConversation.messages.map((m) =>
              m.requestID === action.payload.responseID ? message : m,
            );
          }
        }

        draft.asyncStatus.submit = 'failed';
        draft.metaConversation.messages = messages;
      });
    },

    [conversationClose.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, close: 'loading' },
      };
    },

    [conversationCloseSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;
        const payloadData = action.payload.data;
        if (draftConversation.id === payloadData.id) {
          draftConversation.is_closed = payloadData.is_closed;
          draftConversation.closed_at = payloadData.closed_at;
          draftConversation.created_at = payloadData.created_at;
          draftConversation.closed_by = payloadData.closed_by;
          if (draftConversation.assignment !== null) {
            // conversation.assignment.agent = payloadData.assignment.agent; @FIXME DIXA-2280
            draftConversation.assignment.unassigned_at = payloadData.closed_by;
          }
          draftConversation.assignments = payloadData.assignments;
          draftConversation.live_assesment = payloadData.live_assesment;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
        }

        draft.asyncStatus.close = 'succeeded';
      });
    },

    [conversationCloseFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, close: 'failed' },
      };
    },

    [conversationWsClose.toString()]: (state: ConversationState, action) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation = draft.metaConversation.conversation;
        const params = action.payload.params;
        if (draftConversation.id === params.csid) {
          draftConversation.is_closed = true;
          draftConversation.closed_by = params.closed_by;
          draftConversation.closed_at = params.conversation.closed_at;
          draftConversation.created_at = params.conversation.created_at;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
          draftConversation.status = params.conversation.status;
          draftConversation.live_assesment = params.conversation.live_assesment;
        }
      });
    },

    [conversationWsPending.toString()]: (state: ConversationState, action) => {
      if (
        state.metaConversation.conversation.id === action.payload.params.csid
      ) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            isAssignedToMe:
              action.payload.params.conversation &&
              action.payload.params.conversation.assignment === null
                ? false
                : state.metaConversation.isAssignedToMe,
            conversation: {
              ...state.metaConversation.conversation,
              is_closed:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.is_closed ===
                  'boolean'
                  ? action.payload.params.conversation.is_closed
                  : state.metaConversation.conversation.is_closed,
              assignment:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignment !==
                  undefined
                  ? action.payload.params.conversation.assignment
                  : state.metaConversation.conversation.assignment,
              assignments:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignments !==
                  undefined
                  ? action.payload.params.conversation.assignments
                  : state.metaConversation.conversation.assignments,
              status:
                action.payload.params.conversation &&
                action.payload.params.conversation.status
                  ? action.payload.params.conversation.status
                  : state.metaConversation.conversation.status,
              pending_expiration_at:
                action.payload.params.conversation &&
                action.payload.params.conversation.pending_expiration_at !==
                  undefined
                  ? action.payload.params.conversation.pending_expiration_at
                  : null,
              live_assesment:
                action.payload.params.conversation &&
                action.payload.params.conversation.live_assesment !== undefined
                  ? action.payload.params.conversation.live_assesment
                  : null,
            },
          },
        };
      }
      return state;
    },

    [conversationWsAwaitingPending.toString()]: (
      state: ConversationState,
      action,
    ) => {
      if (
        state.metaConversation.conversation.id === action.payload.params.csid
      ) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            isAssignedToMe:
              action.payload.params.conversation &&
              action.payload.params.conversation.assignment === null
                ? false
                : state.metaConversation.isAssignedToMe,
            conversation: {
              ...state.metaConversation.conversation,
              is_closed:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.is_closed ===
                  'boolean'
                  ? action.payload.params.conversation.is_closed
                  : state.metaConversation.conversation.is_closed,
              assignment:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignment !==
                  undefined
                  ? action.payload.params.conversation.assignment
                  : state.metaConversation.conversation.assignment,
              assignments:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignments !==
                  undefined
                  ? action.payload.params.conversation.assignments
                  : state.metaConversation.conversation.assignments,
              status:
                action.payload.params.conversation &&
                action.payload.params.conversation.status
                  ? action.payload.params.conversation.status
                  : state.metaConversation.conversation.status,
              pending_expiration_at:
                action.payload.params.conversation &&
                action.payload.params.conversation.pending_expiration_at !==
                  undefined
                  ? action.payload.params.conversation.pending_expiration_at
                  : null,
              live_assesment:
                action.payload.params.conversation &&
                action.payload.params.conversation.live_assesment !== undefined
                  ? action.payload.params.conversation.live_assesment
                  : null,
            },
          },
        };
      }
      return state;
    },

    [conversationWsCancelPending.toString()]: (
      state: ConversationState,
      action,
    ) => {
      if (
        state.metaConversation.conversation.id === action.payload.params.csid
      ) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            isAssignedToMe:
              action.payload.params.conversation &&
              action.payload.params.conversation.assignment === null
                ? false
                : state.metaConversation.isAssignedToMe,
            conversation: {
              ...state.metaConversation.conversation,
              is_closed:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.is_closed ===
                  'boolean'
                  ? action.payload.params.conversation.is_closed
                  : state.metaConversation.conversation.is_closed,
              assignment:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignment !==
                  undefined
                  ? action.payload.params.conversation.assignment
                  : state.metaConversation.conversation.assignment,
              assignments:
                action.payload.params.conversation &&
                typeof action.payload.params.conversation.assignments !==
                  undefined
                  ? action.payload.params.conversation.assignments
                  : state.metaConversation.conversation.assignments,
              status:
                action.payload.params.conversation &&
                action.payload.params.conversation.status
                  ? action.payload.params.conversation.status
                  : state.metaConversation.conversation.status,
              pending_expiration_at:
                action.payload.params.conversation &&
                action.payload.params.conversation.pending_expiration_at !==
                  undefined
                  ? action.payload.params.conversation.pending_expiration_at
                  : null,
              live_assesment:
                action.payload.params.conversation &&
                action.payload.params.conversation.live_assesment !== undefined
                  ? action.payload.params.conversation.live_assesment
                  : null,
            },
          },
        };
      }
      return state;
    },

    [conversationWsOpened.toString()]: (state: ConversationState, action) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation = draft.metaConversation.conversation;
        const params = action.payload.params;
        if (draftConversation.id === params.csid) {
          draftConversation.assignment = params.conversation.assignment;
          draftMetaConv.isAssignedToMe = false;
          draftConversation.is_closed = false;
          draftConversation.closed_by = null;
          draftConversation.closed_at = null;
          draftMetaConv.tempMessage = null;
          draftMetaConv.tempFormValues = null;
          draftMetaConv.lastSeen = null;
          draftConversation.pending_expiration_at = null;
          draftConversation.live_assesment =
            params.conversation &&
            params.conversation.live_assesment !== undefined
              ? params.conversation.live_assesment
              : null;
          draftConversation.status =
            params.conversation && params.conversation.status
              ? params.conversation.status
              : draftConversation.status;
          draftConversation.initial_channel =
            params.conversation && params.conversation.initial_channel
              ? params.conversation.initial_channel
              : draftConversation.initial_channel;
        }
      });
    },

    [conversationAddRating.toString()]: (state: ConversationState, action) => {
      return produce(state, (draft) => {
        const { id, csid, created_at, created_by, message, score, type } =
          action.payload;

        if (draft.metaConversation.conversation.id === csid) {
          const rating = {
            id: id,
            csid: csid,
            created_at: created_at,
            created_by: created_by,
            message: message,
            score: 0 < score && score < 6 ? score : null,
            type: type,
          };

          draft.metaConversation.conversation?.ratings?.push(rating);
        }
      });
    },

    [conversationLinkChild.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, linked: 'loading' },
      };
    },

    [conversationLinkChildFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, linked: 'failed' },
        linkedConversations: state.linkedConversations,
      };
    },

    [conversationLinkChildSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const linkedConversation = emptyMetaConversation();
      const conversation = { ...action.payload.data.history.main_conversation };
      let isAssignedToMe = false;

      if (conversation.linked_to !== state.metaConversation.conversation.id) {
        return state;
      }

      if (
        conversation.assignment &&
        action.payload.request.userID === conversation.assignment.agent_id
      ) {
        isAssignedToMe = true;
      }

      linkedConversation.conversation = conversation;
      linkedConversation.isAssignedToMe = isAssignedToMe;
      linkedConversation.requester =
        conversation.authors[conversation.requested_by] !== undefined
          ? conversation.authors[conversation.requested_by]
          : null;

      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, linked: 'succeeded' },
        metaConversation: {
          ...state.metaConversation,
          linkedConversations: [
            linkedConversation,
            ...state.metaConversation.linkedConversations.filter(
              (metaC: MetaConversation) =>
                metaC.conversation.id !== conversation.id,
            ),
          ],
        },
      };
    },

    [conversationAddWrapup.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, wrapup: 'loading' },
      };
    },

    [conversationAddWrapupSucceeded.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, wrapup: 'succeeded' },
      };
    },

    [conversationAddWrapupFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, wrapup: 'failed' },
      };
    },

    [combineActions(
      transferToNumberWsResponse,
      transferToQueueWsResponse,
      transferToAgentWsResponse,
      appActions.conversationTransferRequest.fulfilled.type,
    ).toString()]: (state: ConversationState, action) => {
      return produce(state, (draft: ConversationState) => {
        // the payload in `appActions.transferWsResponseReceived` action is
        // in `wsResponse`. If `wsResponse` does not exist, it means that
        // the old actions are triggered, so `action.payload should be used
        const { csid, accepted, transferee, isAssignedToMe } =
          action.payload.wsResponse ?? action.payload;
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;

        // We should not update the assignment of the conversation when transferring to non-agents.
        if (transferee?.type === 'contact') {
          draftMetaConv.isAssignedToMe = true;
          return;
        }

        if (draftConversation.id === csid && accepted === true) {
          if (draftConversation.assignment !== null) {
            draftConversation.assignment.agent = transferee;
            const originalAgent = original(draftConversation.assignment);
            if (
              draftConversation.assignments === undefined ||
              draftConversation.assignments === null
            ) {
              draftConversation.assignments = [];
            }
            draftConversation.assignments.push({
              assigned_at: moment.utc().format(),
              agent: originalAgent,
              agent_id: originalAgent.id,
            });
          } else {
            let assignment: any | null = null;
            if (transferee) {
              assignment = {
                agent: transferee,
                agent_id: transferee.id,
                assigned_at: moment.utc().format(),
              };
            }
            draftConversation.assignment = assignment;
          }

          if (typeof isAssignedToMe === 'boolean') {
            draftMetaConv.isAssignedToMe = isAssignedToMe;
          } else {
            draftMetaConv.isAssignedToMe = false;
          }
        }
      });
    },

    [conversationRespondeToOfferAcceptedSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation = draft.metaConversation.conversation;
        const { csid, accepted_by } = action.payload.data;

        if (draftConversation.id === csid) {
          if (draftConversation.assignment) {
            draftConversation.assignment.agent = accepted_by;
            draftConversation.assignment.agent_id = accepted_by.id;
            draftConversation.assignment.assigned_at = moment.utc().format();
          } else {
            draftConversation.assignment = {
              agent: accepted_by,
              agent_id: accepted_by.id,
              assigned_at: moment.utc().format(),
            };
          }
          draftMetaConv.isAssignedToMe = true;
        }
      });
    },

    [conversationToPending.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, pending: 'loading' },
      };
    },

    [conversationToPendingSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation = draft.metaConversation.conversation;
        const payloadData = action.payload.data;

        if (draftConversation.id === payloadData.id) {
          draftMetaConv.isAssignedToMe =
            payloadData.assignment === null
              ? false
              : draftMetaConv.isAssignedToMe;
          draftConversation.is_closed = payloadData.is_closed;
          draftConversation.status = payloadData.status;
          draftConversation.assignment = payloadData.assignment;
          draftConversation.assignments = payloadData.assignments;
          draftConversation.pending_expiration_at =
            payloadData.pending_expiration_at;
        }

        draft.asyncStatus.pending = 'succeeded';
      });
    },

    [conversationToPendingFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, pending: 'failed' },
      };
    },

    [conversationCancelPending.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, pending: 'loading' },
      };
    },

    [conversationCancelPendingSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftConversation = draft.metaConversation.conversation;
        const payloadData = action.payload.data;
        if (draftConversation.id === payloadData.id) {
          draftConversation.is_closed =
            payloadData.is_closed || draftConversation.is_closed;
          draftConversation.status =
            payloadData.status || draftConversation.status;
          draftConversation.pending_expiration_at =
            (payloadData.pending_expiration_at !== undefined
              ? payloadData.pending_expiration_at
              : draftConversation.pending_expiration_at) || null;
        }

        draft.asyncStatus.pending = 'succeeded';
      });
    },

    [conversationCancelPendingFailed.toString()]: (
      state: ConversationState,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, pending: 'failed' },
      };
    },

    [conversationPauseCallRecording.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, recording: 'loading' },
      };
    },

    [conversationPauseCallRecordingSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv: any = draft.metaConversation;
        if (
          draftMetaConv.conversation.id === action.payload.request.csid &&
          action.payload.data.success === true
        ) {
          draftMetaConv.conversation.attributes.recording_pause_status =
            'paused';
        }

        draft.asyncStatus.recording = 'succeeded';
      });
    },

    [conversationPauseCallRecordingFailed.toString()]: (
      state: ConversationState,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, recording: 'failed' },
      };
    },

    [conversationResumeCallRecording.toString()]: (
      state: ConversationState,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, recording: 'loading' },
      };
    },

    [conversationResumeCallRecordingSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv: any = draft.metaConversation;
        if (
          draftMetaConv.conversation.id === action.payload.request.csid &&
          action.payload.data.success === true
        ) {
          draftMetaConv.conversation.attributes.record_conversation = 'true';
          delete draftMetaConv.conversation.attributes.recording_pause_status;
        }

        draft.asyncStatus.recording = 'succeeded';
      });
    },

    [conversationResumeCallRecordingFailed.toString()]: (
      state: ConversationState,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, recording: 'failed' },
      };
    },

    [transferToNumberBlindRequestSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft) => {
        const csid = action.payload.data.id;

        // The request has been successfully transfer to another number.
        if (draft.metaConversation.conversation.id === csid) {
          draft.metaConversation.isAssignedToMe = false;
        }
      });
    },

    [transferToAgentAssistedResolveSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft) => {
        const csid = action.payload.data.csid;

        // We have accepted an assisted transfer from another agent.
        if (draft.metaConversation.conversation.id === csid) {
          const accepted = action.payload.data.accepted;

          if (
            draft.metaConversation.isAssignedToMe === true &&
            accepted === true
          ) {
            draft.metaConversation.isAssignedToMe = false;
          }
        }
      });
    },

    [conversationWsAgentAssigned.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;
        const csid = action.payload.csid;
        const newAgent = action.payload.agent;
        const isAssignedToMe = action.payload.isAssignedToMe;
        // We only update if we have the csid on the current conversation view and conversation not in an internal call.
        if (
          draftConversation.id === csid &&
          draftConversation.direction !== 'internal'
        ) {
          if (draftConversation.assignment !== null) {
            draftConversation.assignment.agent = newAgent;
            draftConversation.assignment.agent_id = newAgent.id;
            draftConversation.assignment.assigned_at = moment.utc().format();
          } else {
            draftConversation.assignment = {
              agent: newAgent,
              agent_id: newAgent.id,
              assigned_at: moment.utc().format(),
            };
          }
          if (typeof isAssignedToMe === 'boolean') {
            draftMetaConv.isAssignedToMe = isAssignedToMe;
          }
        }
      });
    },

    [conversationWsAgentUnassigned.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;
        const csid = action.payload.csid;
        const unassignedAgent = action.payload.agent;
        const isAssignedToMe = action.payload.isAssignedToMe;
        // We only update if we have the csid on the current conversation view and conversation not in an internal call.
        if (
          draftConversation.id === csid &&
          draftConversation.direction !== 'internal'
        ) {
          if (
            draftConversation.assignment &&
            draftConversation.assignment.agent &&
            unassignedAgent.id === draftConversation.assignment.agent.id
          ) {
            draftConversation.assignment.agent = null;
            draftConversation.assignment.agent_id = null;
            draftConversation.assignment.assigned_at = null;
          }
          if (typeof isAssignedToMe === 'boolean') {
            draftMetaConv.isAssignedToMe = isAssignedToMe;
          }
        }
      });
    },

    [conversationWsReassignmentStatus.toString()]: (
      state: ConversationState,
      action: {
        payload: {
          csid: number;
          new_agent: User;
          old_agent: User;
          isAssignedToMe: boolean;
        };
      },
    ) => {
      return produce(state, (draft: ConversationState) => {
        const draftMetaConv = draft.metaConversation;
        const draftConversation: any = draft.metaConversation.conversation;
        const newAgent = action.payload.new_agent;
        const csid = action.payload.csid;
        const isAssignedToMe = action.payload.isAssignedToMe;
        // We only update if we have the csid on the current conversation view and conversation not in an internal call.
        if (
          draftConversation.id === csid &&
          draftConversation.direction !== 'internal'
        ) {
          if (draftConversation.assignment !== null) {
            draftConversation.assignment.agent = newAgent;
            draftConversation.assignment.agent_id = newAgent.id;
            draftConversation.assignment.assigned_at = moment.utc().format();
          } else {
            draftConversation.assignment = {
              agent: newAgent,
              agent_id: newAgent.id,
              assigned_at: moment.utc().format(),
            };
          }
          if (typeof isAssignedToMe === 'boolean') {
            draftMetaConv.isAssignedToMe = isAssignedToMe;
          }
        }
      });
    },

    [conversationWsEndUserPresence.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const csid = action.payload.params.csid;
      const isConnected = action.payload.params.is_connected;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            conversation: {
              ...state.metaConversation.conversation,
              live_assesment: {
                is_end_user_connected: isConnected,
                timestamp: moment.utc().format(),
              },
            },
          },
        };
      }

      return state;
    },

    [conversationSaveTempFormValues.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, formValues } = action.payload;
      if (
        state.metaConversation &&
        state.metaConversation.conversation.id === csid
      ) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            tempFormValues: formValues,
          },
        };
      }
      return state;
    },

    [conversationSaveTempWrapup.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, message } = action.payload;

      if (state.metaConversation?.conversation.id === csid) {
        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.tempWrapup = message;
        });
      }

      return state;
    },

    [conversationSaveTempCurrentTab.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, currentTab } = action.payload;

      if (state.metaConversation?.conversation.id === csid) {
        return produce(state, (draft: ConversationState) => {
          draft.metaConversation.tempCurrentTab = currentTab;
        });
      }

      return state;
    },

    [feedbackRequest.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, feedback: 'loading' },
        feedback: [],
      };
    },

    [feedbackRequestSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, feedback: 'succeeded' },
        feedback: action.payload.data,
      };
    },

    [feedbackRequestFailed.toString()]: (state: ConversationState) => {
      return {
        ...state,
        asyncStatus: { ...state.asyncStatus, feedback: 'failed' },
        feedback: [],
      };
    },

    [getAvailableSatisfactionLanguagesRequest.toString()]: (
      state: ConversationState,
    ) => {
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.availableSatisfactionLanguages = 'loading';
        draft.availableSatisfactionLanguages = [];
      });
    },

    [getAvailableSatisfactionLanguagesRequestSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.availableSatisfactionLanguages = 'succeeded';
        draft.availableSatisfactionLanguages = action.payload.data.languages;
      });
    },

    [getAvailableSatisfactionLanguagesRequestFailed.toString()]: (
      state: ConversationState,
    ) => {
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.availableSatisfactionLanguages = 'failed';
        draft.availableSatisfactionLanguages = [];
      });
    },

    [cancelSatisfactionRequest.toString()]: (state: ConversationState) => {
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.cancelSatisfaction = 'loading';
      });
    },

    [cancelSatisfactionRequestSucceeded.toString()]: (
      state: ConversationState,
    ) => {
      ampli.conversationCsatCancelled();
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.cancelSatisfaction = 'succeeded';
      });
    },

    [cancelSatisfactionRequestFailed.toString()]: (
      state: ConversationState,
    ) => {
      return produce(state, (draft: ConversationState) => {
        draft.asyncStatus.cancelSatisfaction = 'failed';
      });
    },

    [conversationWsLanguageUpdated.toString()]: (
      state: ConversationState,
      action,
    ) => {
      return produce(state, (draft: any) => {
        draft.metaConversation.conversation.attributes['language'] =
          action.payload.language;
      });
    },

    [conversationWsNewPartyAdded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [user.id.toString()]: {
                ...state.metaConversation.extraParties[user.id],
                user,
                callState: 'inProgress',
              },
            },
          },
        };
      }
      return state;
    },

    [conversationWsNewPartyRemoved.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        const newExtraParties = { ...state.metaConversation.extraParties };
        delete newExtraParties[user.id];
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: newExtraParties,
          },
        };
      }
      return state;
    },

    [conversationWsNewPartyRinging.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [user.id.toString()]: {
                ...state.metaConversation.extraParties[user.id],
                user,
                callState: 'ringing',
              },
            },
          },
        };
      }
      return state;
    },

    [conversationWsNewPartyBusy.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [user.id.toString()]: {
                ...state.metaConversation.extraParties[user.id],
                user,
                callState: 'busy',
              },
            },
          },
        };
      }
      return state;
    },

    [conversationWsNewPartyFailed.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [user.id.toString()]: {
                ...state.metaConversation.extraParties[user.id],
                user,
                callState: 'failed',
              },
            },
          },
        };
      }
      return state;
    },

    [conversationWsNewPartyNoAnswer.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, user } = action.payload;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [user.id.toString()]: {
                ...state.metaConversation.extraParties[user.id],
                user,
                callState: 'noAnswer',
              },
            },
          },
        };
      }
      return state;
    },

    [addCallerSetOnHoldRequest.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { userId } = action.payload;
      return {
        ...state,
        asyncStatus: {
          ...state.asyncStatus,
          holdCaller: {
            ...state.asyncStatus.holdCaller,
            [userId.toString()]: 'loading',
          },
        },
      };
    },

    [addCallerSetOnHoldRequestSucceeded.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, userId, hold } = action.payload.meta.request;
      if (state.metaConversation.conversation.id === csid) {
        return {
          ...state,
          asyncStatus: {
            ...state.asyncStatus,
            holdCaller: {
              ...state.asyncStatus.holdCaller,
              [userId.toString()]: 'succeeded',
            },
          },
          metaConversation: {
            ...state.metaConversation,
            extraParties: {
              ...state.metaConversation.extraParties,
              [userId.toString()]: {
                ...state.metaConversation.extraParties[userId],
                onHold: hold,
              },
            },
          },
        };
      }
      return state;
    },

    [addCallerSetOnHoldRequestFailed.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { userId } = action.payload.action.payload;
      return {
        ...state,
        asyncStatus: {
          ...state.asyncStatus,
          holdCaller: {
            ...state.asyncStatus.holdCaller,
            [userId.toString()]: 'failed',
          },
        },
      };
    },

    [conversationWsMessageDeliveryStatusUpdated.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { csid, messageId, delivery } = action.payload.params;

      return produce(state, (draft) => {
        if (draft.metaConversation.conversation.id === csid) {
          draft.metaConversation.conversation.message_delivery_status = {
            ...draft.metaConversation.conversation.message_delivery_status,
            [messageId]: delivery,
          };
        }
      });
    },

    [conversationWsOutboundMessageStatusUpdated.toString()]: (
      state: ConversationState,
      action,
    ) => {
      const { outbound_enabled } = action.payload.params;

      return {
        ...state,
        metaConversation: {
          ...state.metaConversation,
          conversation: {
            ...state.metaConversation.conversation,
            attributes: {
              ...state.metaConversation.conversation.attributes,
              outbound_enabled,
            },
          },
        },
      };
    },
    [conversationSetFocusMode.toString()]: (state: ConversationState, action) =>
      produce(state, (draft) => {
        draft.isFocusMode = action.payload;
      }),

    [conversationSetSmallLayoutSidebarOpen.toString()]: (
      state: ConversationState,
      action,
    ) =>
      produce(state, (draft) => {
        draft.isSmallLayoutSidebarOpen = action.payload;
      }),

    [conversationUpdateUser.toString()]: (state: ConversationState, action) => {
      return produce(state, (draftState: ConversationState) => {
        const {
          payload: { data: updatedUser },
        } = action as { payload: { data: User } };

        draftState.metaConversation = updateUserInMetaConversation(
          draftState.metaConversation,
          updatedUser,
        );
      });
    },

    [conversationSetSummary.toString()]: (state: ConversationState, action) => {
      return produce(state, (draftState: ConversationState) => {
        if (draftState.metaConversation.conversation.summary) {
          draftState.metaConversation.conversation.summary.current =
            action.payload;
        }
      });
    },
    [conversationSubjectUpdate.toString()]: (
      state: ConversationState,
      action,
    ) =>
      produce(state, (draftState: ConversationState) => {
        const { csid, subject } = action.payload;
        if (draftState.metaConversation.conversation.id === csid) {
          if (!draftState.metaConversation.conversation.attributes) {
            draftState.metaConversation.conversation.attributes = {};
          }
          draftState.metaConversation.conversation.attributes.subject = subject;
        }
      }),
  },
  initialState,
);

export default conversation;
